import React, { useState } from "react"
import {
  Box,
  Flex,
} from "@chakra-ui/react"
import { UseFormWatch } from "react-hook-form"
import { WalletPassBuilderFormValues } from "./walletPassBuilderTypes"
import { resolvePassPreviewProps } from "./walletPassDataMappers"
import WALLET_PASS_BUILDER_COPY from "./walletPassBuilderCopy"
import AppleWalletPassPreview, { getApplePassPreviewHeight } from "./AppleWalletPassPreview"
import AppleWalletPassBackPreview from "./AppleWalletPassBackPreview"
import GoogleWalletPassPreview, { getGooglePassPreviewHeight } from "./GoogleWalletPassPreview"
import GoogleWalletPassBackPreview from "./GoogleWalletPassBackPreview"
import {
  PassBuilderPreviewSectionTitle,
} from "./PassBuilderTypography"
import PassBuilderPlatformSwitcher, {
  type PassBuilderPlatform,
} from "./PassBuilderPlatformSwitcher"
import PassBuilderPreviewScale from "./PassBuilderPreviewScale"
import {
  PASS_BUILDER_PREVIEW_PASS_SCALE_WIDTH,
  PASS_BUILDER_PREVIEW_PASS_TO_BACK_GAP,
  PASS_BUILDER_PREVIEW_SCROLL_PADDING_BOTTOM,
  PASS_BUILDER_PREVIEW_SECTION_TITLE_MB,
  PASS_BUILDER_PREVIEW_SIDEBAR_TOP_PADDING,
  PASS_BUILDER_PREVIEW_TAB_TO_SECTION_GAP,
} from "./passBuilderTokens"

type Props = {
  watch: UseFormWatch<WalletPassBuilderFormValues>
  eventName: string
  eventAccountName?: string
  eventDateText?: string
  eventTimeText?: string
  eventVenue?: string
  eventAddress?: string
}

const WalletPassBuilderPreview = ({
  watch,
  eventName,
  eventAccountName,
  eventDateText,
  eventTimeText,
  eventVenue,
  eventAddress,
}: Props) => {
  const [platform, setPlatform] = useState<PassBuilderPlatform>("apple")

  const values = watch()

  const {
    applePreviewProps, googlePreviewProps, googleDetailFields, backSections,
  } = resolvePassPreviewProps(values, {
    name: eventName,
    accountName: eventAccountName,
    dateText: eventDateText,
    timeText: eventTimeText,
    venue: eventVenue,
    address: eventAddress,
  })

  const frontPassBaseHeight = platform === "google"
    ? getGooglePassPreviewHeight(
      googlePreviewProps.heroImageUrl,
      googlePreviewProps.auxiliaryRow.length > 0,
    )
    : getApplePassPreviewHeight()

  return (
    <Flex direction="column" h="full">
      <Box
        position="sticky"
        top={0}
        bg="gray.50"
        zIndex={5}
        pt={PASS_BUILDER_PREVIEW_SIDEBAR_TOP_PADDING}
        pb={0}
      >
        <Flex justify="center">
          <PassBuilderPlatformSwitcher
            value={platform}
            onChange={setPlatform}
          />
        </Flex>
      </Box>

      <Box flex={1} overflowY="auto" pb={PASS_BUILDER_PREVIEW_SCROLL_PADDING_BOTTOM}>
        <Flex direction="column" align="center" w="full">
          <Box w={`${PASS_BUILDER_PREVIEW_PASS_SCALE_WIDTH}px`} maxW="full">
            <PassBuilderPreviewSectionTitle
              mt={PASS_BUILDER_PREVIEW_TAB_TO_SECTION_GAP}
              mb={PASS_BUILDER_PREVIEW_SECTION_TITLE_MB}
            >
              {WALLET_PASS_BUILDER_COPY.preview.frontTitle}
            </PassBuilderPreviewSectionTitle>
            <PassBuilderPreviewScale baseHeight={frontPassBaseHeight}>
              {platform === "google" ? (
                <GoogleWalletPassPreview {...googlePreviewProps} />
              ) : (
                <AppleWalletPassPreview {...applePreviewProps} />
              )}
            </PassBuilderPreviewScale>

            <PassBuilderPreviewSectionTitle
              mt={PASS_BUILDER_PREVIEW_PASS_TO_BACK_GAP}
              mb={PASS_BUILDER_PREVIEW_SECTION_TITLE_MB}
            >
              {WALLET_PASS_BUILDER_COPY.preview.backTitle}
            </PassBuilderPreviewSectionTitle>
            <PassBuilderPreviewScale>
              {platform === "google" ? (
                <GoogleWalletPassBackPreview
                  {...googlePreviewProps}
                  auxiliaryFields={googleDetailFields}
                  sections={backSections}
                />
              ) : (
                <AppleWalletPassBackPreview
                  {...applePreviewProps}
                  sections={backSections}
                  eventTitle={eventName}
                />
              )}
            </PassBuilderPreviewScale>
          </Box>
        </Flex>
      </Box>
    </Flex>
  )
}

export default WalletPassBuilderPreview
